import type { UVInsertConfig } from "../../models/uv-inserts"

/**
 * Min/max price range in pounds, derived from the theme variants of a set of UV insert configs.
 * The caller is responsible for currency formatting — this layer stays locale-agnostic.
 */
export type UvInsertPriceRange = {
  /** Lowest price in pounds (e.g. 4.99). */
  minPounds: number
  /** Highest price in pounds across all relevant config themes. Equal to minPounds when all prices match. */
  maxPounds: number
}

/**
 * Collects theme variant prices from a specified subset of UV insert configs and returns
 * the min/max range in pounds.
 *
 * Liquid variant prices are in the smallest currency unit (pence), and may be a string or
 * number (e.g. 499 or "499" = £4.99). Returns null when no valid prices are found.
 *
 * Pure function — no window access, no formatting.
 */
export function getUvInsertPriceRange(options: {
  configs: UVInsertConfig[]
  configHandles: string[]
}): UvInsertPriceRange | null {
  const { configs, configHandles } = options

  const relevantConfigs = configs.filter((c) => configHandles.includes(c.handle))

  const prices: number[] = []
  for (const config of relevantConfigs) {
    for (const theme of config.themes) {
      const raw = theme.price
      if (raw == null) continue
      const pence = parseFloat(String(raw))
      if (!Number.isNaN(pence) && pence > 0) {
        prices.push(pence / 100)
      }
    }
  }

  if (prices.length === 0) return null

  return {
    minPounds: Math.min(...prices),
    maxPounds: Math.max(...prices),
  }
}
